import type { IToBotMessagePacket, IMessage, t_message_direction } from '../types/network';
import type { TMessage } from '../types/bot_interface';

import { marked } from 'marked';

import f1 from '@eab-pip/pi-f1';
import { KnowledgeBot } from '../core/knowledge_bot';

type t_message_substitution = (msg: string, bot: KnowledgeBot, config: { marked: typeof marked }) => string;

/**
 * store messages send from a network to an botInterface
 */
export class MessagingFacade {
  /**
   * all error messages we show the user
   */
  readonly errors = {
    CONN_FAIL: () => 'I was not able to send your message to our Knowledge Center; you can try again.'
  };

  /**
   * receive the list of messages from the KnowledgeBot
   */
  constructor(private messages: IMessage[], private bot: KnowledgeBot) {}

  /**
   * send a message from the human to the bot
   */
  sendMessageToBot(text: string, permission: 'public' | 'private' = 'public') {
    if (f1.is.empty(text)) {
      return {} as IToBotMessagePacket;
    }

    // sanitize content to/from bot
    //
    text = this.bot.security.sanitizeContent(text);

    // on every message sent keep the kbi alive
    //
    this.bot.security.keepAlive();

    if (permission === 'public') {
      this.writeMessage(text, 'to-bot');
    }

    return this._prepareMessageForBot(text, permission);
  }

  /**
   * send a message from the human to the bot
   */
  protected _prepareMessageForBot(text: string, permission: IToBotMessagePacket['permission'] = 'public') {
    const formattedMessages = this.messages.map((msg) => {
      return msg.type === 'to-bot' ? `Human: ${msg.content}` : `Assistant: ${msg.content}`;
    });

    const detail = {
      permission,
      messages_formatted: formattedMessages,
      messages: this.messages,
      values: this.messages,
      message: text,
      value: text
    } as IToBotMessagePacket;

    return detail;
  }

  /**
   * add additional content from the bot to the current message
   * @see MessagingFacade#writeMessageFromBot
   */
  amendMessageFromBot(text: string, mode: 'delayed' | 'immediate' = 'immediate') {
    let message: IMessage = f1.nth(0, f1.last(this.messages));

    if (this.afterMessageAmended) {
      message = this.afterMessageAmended(message) ?? message;
    }

    if (message) {
      message.raw += text;

      // only `\n` or `--` prompts to render
      //
      const isEndingCharacter = text === '\n';
      const isImmediate = mode === 'immediate';

      if (isImmediate || (!isImmediate && isEndingCharacter)) {
        message.content = this.processMessage(message.raw, 'from-bot');
      }
    }

    return this.messages;
  }

  /**
   * notify the bot that the streaming is finished
   */
  notifyStreamingDone(token: string) {
    const lastMessage = f1.pipe([f1.filter((msg) => msg.type === 'from-bot'), f1.last, f1.nth(0)], this.messages);
    const message = { id: '', content: '', raw: '', type: 'message-done', token, lastMessage } as IMessage;

    this.messages.push(message);
    if (this.afterMessageDone) {
      this.afterMessageDone(message);
    }
  }

  /**
   * receive a bot message or send a complete bot message
   */
  writeMessageFromBot(text: string) {
    return this.writeMessage(text, 'from-bot');
  }

  /**
   * @param afterAction this helps to short-circuit then functions
   *
   * write an error message to the human
   */
  writeError(text: string, afterAction?: 'throw-error') {
    if (this.afterMessageFailed) {
      this.afterMessageFailed(text);
    }

    if (afterAction === 'throw-error') {
      throw `stop processing response`;
    }
  }

  /**
   * update the feedback for a message
   */
  updateMessages(index: number, item: { key: keyof IMessage; value: boolean }) {
    (this.messages[index] as any)[item.key] = item.value;

    if (this.afterMessageDone) {
      this.messages[index] = this.afterMessageDone(this.messages[index]) ?? this.messages[index];
    }
  }

  /**
   * write a new and complete message, either:
   *
   * - `to-bot` which sends to the bot
   * - `from-bot` which receives from the bot
   *
   * @param type control the direction of message sending
   * @note this is presentation
   */
  writeMessage(raw: string, type: t_message_direction, message: Partial<TMessage> = {}) {
    // sanitize content to/from bot
    //
    raw = this.bot.security.sanitizeContent(raw);

    let newMessage: IMessage = {
      raw,
      type,
      id: f1.id(),
      content: this.processMessage(raw, type),
      timestamp:
        message.timestamp ??
        new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: 'numeric',
          second: undefined
        })
    };

    if (this.afterMessageAdded) {
      newMessage = this.afterMessageAdded(newMessage) ?? newMessage;
    }

    this.messages.push(newMessage);
    return this.messages;
  }

  /**
   * @param error this helps to debug the error
   *
   * console log the error message
   */
  logErrorMessage(error: string) {
    // if there are errors in network, then we should let the user know
    //
    // this.writeMessage(error, 'to-human');
    console.error(`KBI (error):: ${error}`);
  }

  /**
   * convert markdown to html by default, or apply interceptors if present
   * force links to tab always
   * @returns processed message
   */
  protected processMessage(markdown: string, type: t_message_direction): string {
    const substitutions: t_message_substitution[] = f1.copy(
      f1.read(`/substitutions/messages_${f1.form('kebab', 'snake', type)}`, [this.__mdToHTML], this.bot)
    );

    substitutions.push(this.__forceLinksToTab);

    let composed = markdown;
    for (const fn of substitutions) {
      composed = fn(composed, this.bot, { marked });
    }

    return composed;
  }

  /**
   * @returns html from markdown
   */
  private __mdToHTML(markdown: string) {
    return `${marked.parse(`${markdown || ''}`, { mangle: false, headerIds: false })}`;
  }

  /**
   * @note this is necessary because if not, then the session will die from the ws-kb server
   * @returns <a> with `<a target rel>` so it opens in a new tab
   */
  private __forceLinksToTab(html: string) {
    return html.replace(/<a/g, () => `<a rel="external nofollow noopener noreferrer" target="_blank"`);
  }

  /**
   * an event handler for when a new message is added (or begun)
   */
  afterMessageAdded: (message: IMessage) => IMessage;

  /**
   * an event handle for when a new message is updated (or amended)
   */
  afterMessageAmended: (message: IMessage) => IMessage;

  /**
   * an event handler for when the message streaming is finished
   */
  afterMessageDone: (message: IMessage) => IMessage;

  /**
   * an error handler for when the system fails either when sending or receiving a message
   */
  afterMessageFailed: (text: string) => void;
}
